/**
 * option-price-labels.js
 * Renders the incremental price of each variant option value beneath its swatch.
 *
 * Reads the variant matrix the theme already prints as
 *   <script id="all-variant-data-{unique_id}" type="application/json">
 * inside <variant-radios> / <variant-selects> / <variant-mixed-inputs>.
 *
 * Assumes ADDITIVE pricing (each option's surcharge is independent of the
 * other options). Inconsistencies are logged to the console in design mode.
 */

const CONFIG = {
  // Show a label on the baseline (cheapest) value of each axis?
  showBaseline: false,
  // Text used when showBaseline is true.
  baselineText: 'Included',
  // Hide the whole axis if every value costs the same (e.g. a pure colour axis).
  hideFlatAxes: true,
  // Prefix for positive deltas.
  plus: '+',
};

/* ---------- money formatting ---------- */

function formatMoney(cents, format) {
  const fmt = format || window.OPTION_PRICE_MONEY_FORMAT || '${{amount}}';
  const negative = cents < 0;
  const value = Math.abs(cents);

  function withDelimiters(number, precision, thousands, decimal) {
    precision = precision ?? 2;
    thousands = thousands ?? ',';
    decimal = decimal ?? '.';
    if (isNaN(number) || number === null) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = fmt.match(placeholder);
  let formatted = fmt;

  if (match) {
    let body;
    switch (match[1]) {
      case 'amount':
        body = withDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        body = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        body = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        body = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        body = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        body = withDelimiters(value, 0, ' ', '');
        break;
      case 'amount_with_period_and_space_separator':
        body = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        body = withDelimiters(value, 2);
    }
    formatted = fmt.replace(placeholder, body);
  }

  return (negative ? '\u2212' : '') + formatted;
}

/* ---------- delta computation ---------- */

function optionKey(variant) {
  return [variant.option1, variant.option2, variant.option3];
}

/**
 * For each option position, find the surcharge of every value relative to
 * that axis's baseline (the value with the lowest mean variant price).
 *
 * Method: group variants that are identical on every axis EXCEPT the one
 * being measured, then read the price difference inside each group. With
 * additive pricing every group agrees; we take the most common answer and
 * warn if they disagree.
 */
function computeDeltas(variants) {
  const available = variants.filter((v) => v && typeof v.price === 'number');
  if (!available.length) return [];

  const positions = [0, 1, 2].filter((i) => available.some((v) => optionKey(v)[i] != null));
  const result = [];

  positions.forEach((pos) => {
    // Baseline = value with the lowest average price across all variants.
    const sums = new Map();
    available.forEach((v) => {
      const val = optionKey(v)[pos];
      if (val == null) return;
      const entry = sums.get(val) || { total: 0, count: 0 };
      entry.total += v.price;
      entry.count += 1;
      sums.set(val, entry);
    });

    let baseline = null;
    let baselineMean = Infinity;
    sums.forEach((entry, val) => {
      const mean = entry.total / entry.count;
      if (mean < baselineMean) {
        baselineMean = mean;
        baseline = val;
      }
    });

    // Group by the other axes, then diff within each group.
    const groups = new Map();
    available.forEach((v) => {
      const key = optionKey(v)
        .map((val, i) => (i === pos ? '\u0000' : val))
        .join('\u001f');
      const bucket = groups.get(key) || [];
      bucket.push(v);
      groups.set(key, bucket);
    });

    const observations = new Map(); // value -> [delta, delta, ...]
    groups.forEach((bucket) => {
      const base = bucket.find((v) => optionKey(v)[pos] === baseline);
      if (!base) return;
      bucket.forEach((v) => {
        const val = optionKey(v)[pos];
        if (val == null) return;
        const list = observations.get(val) || [];
        list.push(v.price - base.price);
        observations.set(val, list);
      });
    });

    const deltas = new Map();
    let consistent = true;
    observations.forEach((list, val) => {
      const tally = new Map();
      list.forEach((d) => tally.set(d, (tally.get(d) || 0) + 1));
      let best = 0;
      let bestCount = -1;
      tally.forEach((count, d) => {
        if (count > bestCount) {
          bestCount = count;
          best = d;
        }
      });
      if (tally.size > 1) consistent = false;
      deltas.set(val, best);
    });

    // Fallback for values never paired with the baseline: use mean difference.
    sums.forEach((entry, val) => {
      if (!deltas.has(val)) {
        deltas.set(val, Math.round(entry.total / entry.count - baselineMean));
      }
    });

    let flat = true;
    deltas.forEach((d) => {
      if (d !== 0) flat = false;
    });

    result[pos] = { baseline, deltas, consistent, flat };
  });

  return result;
}

/* ---------- DOM application ---------- */

function readVariants(container) {
  const node = container.querySelector('script[id^="all-variant-data-"]');
  if (!node) return null;
  try {
    const parsed = JSON.parse(node.textContent);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

function labelFor(delta, axis) {
  if (delta === 0) {
    return CONFIG.showBaseline ? CONFIG.baselineText : null;
  }
  const money = formatMoney(Math.abs(delta));
  return (delta > 0 ? CONFIG.plus : '\u2212') + money;
}

function applyToRadios(container, axes) {
  // Group radios by DOM order of their fieldset/selector wrapper.
  const inputs = Array.from(container.querySelectorAll('input.product-form__swatch__input'));
  if (!inputs.length) return false;

  const groupOrder = [];
  inputs.forEach((input) => {
    if (!groupOrder.includes(input.name)) groupOrder.push(input.name);
  });

  inputs.forEach((input) => {
    const pos = groupOrder.indexOf(input.name);
    const axis = axes[pos];
    if (!axis) return;
    if (axis.flat && CONFIG.hideFlatAxes) return;

    const delta = axis.deltas.get(input.value);
    if (delta === undefined) return;

    const text = labelFor(delta, axis);
    if (text === null) return;

    const wrapper =
      input.closest('.product-form__swatch__item') || input.parentElement;
    if (!wrapper) return;
    if (wrapper.querySelector(':scope > .option-price-label')) return;

    const span = document.createElement('span');
    span.className = 'option-price-label';
    span.setAttribute('aria-hidden', 'false');
    span.textContent = text;
    wrapper.classList.add('has-option-price-label');
    wrapper.appendChild(span);
  });

  return true;
}

function applyToSelects(container, axes) {
  const selects = Array.from(container.querySelectorAll('select.single-option-selector'));
  if (!selects.length) return false;

  selects.forEach((select, pos) => {
    const axis = axes[pos];
    if (!axis) return;
    if (axis.flat && CONFIG.hideFlatAxes) return;

    Array.from(select.options).forEach((opt) => {
      if (opt.dataset.priceLabelled === 'true') return;
      const delta = axis.deltas.get(opt.value);
      if (delta === undefined) return;
      const text = labelFor(delta, axis);
      if (text === null) return;
      opt.dataset.priceLabelled = 'true';
      // Keep data-base-text in sync so theme JS re-renders with the label.
      const suffix = '  (' + text + ')';
      opt.textContent = opt.textContent.trim() + suffix;
      if (opt.dataset.baseText) opt.dataset.baseText += suffix;
    });
  });

  return true;
}

/**
 * Deltas depend only on the variant matrix, which never changes for a given
 * picker. Cache them per container element so repeated re-renders are cheap.
 * Keyed on the element itself, so a genuinely new picker recomputes.
 */
const AXIS_CACHE = new WeakMap();

function decorate(container) {
  let axes = AXIS_CACHE.get(container);

  if (!axes) {
    const variants = readVariants(container);
    if (!variants || variants.length < 2) return;

    axes = computeDeltas(variants);
    if (!axes.length) return;

    AXIS_CACHE.set(container, axes);

    if (window.Shopify && window.Shopify.designMode) {
      axes.forEach((axis, i) => {
        if (axis && !axis.consistent) {
          console.warn(
            '[option-price-labels] Option ' +
              (i + 1) +
              ' is not additive — labels may not sum to the displayed total.'
          );
        }
      });
    }
  }

  // NOTE: deliberately no "already applied" flag on the container.
  // The theme replaces the picker's inner markup on every variant change,
  // which destroys the labels while leaving the container intact. A sticky
  // flag would block re-injection forever. Both apply functions skip values
  // that already carry a label, so calling this repeatedly is safe and
  // self-healing.
  applyToRadios(container, axes);
  applyToSelects(container, axes);
}

function scan(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope
    .querySelectorAll('variant-radios, variant-selects, variant-mixed-inputs')
    .forEach(decorate);
}

/* ---------- boot ---------- */

const PICKER_SELECTOR = 'variant-radios, variant-selects, variant-mixed-inputs';

function boot() {
  scan(document);

  let queued = false;
  let observer = null;

  function rescan() {
    // Our own appendChild calls would re-trigger the observer. Detach while
    // writing, then reattach — avoids a feedback loop.
    if (observer) observer.disconnect();
    try {
      scan(document);
    } finally {
      if (observer) {
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      rescan();
    });
  }

  observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((m) => {
      // Case 1: a whole picker was inserted (quick-view, drawer, section load).
      const added = Array.from(m.addedNodes).some(
        (n) =>
          n.nodeType === 1 &&
          (n.matches?.(PICKER_SELECTOR) || n.querySelector?.(PICKER_SELECTOR))
      );
      if (added) return true;

      // Case 2: the theme re-rendered the INSIDE of an existing picker on
      // variant change. The added nodes are swatch divs, not pickers, so the
      // test above misses them — check the mutation target's ancestry instead.
      const target = m.target;
      if (target && target.nodeType === 1 && target.closest?.(PICKER_SELECTOR)) {
        return true;
      }

      return false;
    });

    if (relevant) schedule();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Belt and braces: some themes swap markup in ways that batch oddly, and
  // section reloads in the theme editor bypass body mutations entirely.
  document.addEventListener('change', (e) => {
    if (e.target?.closest?.(PICKER_SELECTOR)) schedule();
  });
  ['variant:change', 'variantChange', 'product:variant-change'].forEach((evt) => {
    document.addEventListener(evt, schedule);
  });
  document.addEventListener('shopify:section:load', schedule);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}